import React, { Component, useEffect } from 'react';
import { Route, Redirect } from 'react-router';
import Layout from './components/Layout';
import Home from './components/Home';
import { FetchActions } from './components/FetchActions';
import { FetchGigs } from './components/FetchGigs';
import { FetchGroups } from './components/Groups/FetchGroups';
import { FetchInvitations } from './components/FetchInvitations';
import { FetchPolicies } from './components/FetchPolicies';
import { FetchPositions } from './components/FetchPositions';
import { FetchPosts } from './components/FetchPosts';
import { FetchSubscriptions } from './components/FetchSubscriptions';
import { FetchTopics } from './components/FetchTopics';
import { UserSearch } from './components/User/UserSearch';
import { FetchVotes } from './components/User/FetchVotes';
import ForSalePage from './components/Marketplace/ForSale/ForSalePage';
import GroupAssetPage from './components/Groups/GroupAssetPage';
import GroupPublicPage from './components/Groups/GroupPublicPage';
import GroupMembersPage from './components/Groups/GroupMembersPage';
import ChurchInviteJoinPage from './components/Groups/ChurchInviteJoinPage';
import FindChurchPage from './components/Groups/FindChurchPage';
import GroupFinancePage, { StripeConnectReturn } from './components/Groups/GroupFinancePage';
import MinistrySupportPage from './components/Groups/MinistrySupportPage';
import FellowshipEventPage from './components/Groups/FellowshipEventPage';
import ConflictDetails from './components/ConflictDetails';
import { JobsPage } from './components/Marketplace/Jobs/JobsPage';
import { PostCampaign } from './components/PostCampaign';
import { PostGroup } from './components/Groups/PostGroup';
import { PostSubscription } from './components/PostSubscription';
import RealEstatePage from './components/Marketplace/RealEstate/RealEstatePage';
import ServicesPage from './components/Marketplace/Services/ServicesPage';
import { Switch, withRouter } from 'react-router-dom';
import UserProfile from './components/User/UserProfile';
import SettingsPage from './components/User/SettingsPage';
import Dashboard from './components/User/user-Dashboard';
import LearnPage from './components/User/LearnPage';
import MediaPage from './components/User/MediaPage';
import NeedsPage from './components/Needs/NeedsPage';
import VotingHistoryPage from './components/User/VotingHistoryPage';
import TradingHistoryPage from './components/User/TradingHistoryPage';
import { UserAssetPage } from './components/User/UserAssetPage';
import { withAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import ProfileSetupFormPage from './components/User/ProfileSetupFormPage';
import { Chat } from './components/Chat';
import ActionRefinementPage from "./components/action-refinement/action-refinement-page";
import PolicyRefinementPage from "./components/policy-refinement/PolicyRefinementPage";
import PositionRefinementPage from "./components/position-refinement/PositionRefinementPage";
import CalendarEventRefinementPage from "./components/calendar-event-refinement/CalendarEventRefinementPage";
import './styles/global/custom.css';
import SheafSuccess from './components/SheafSuccess';
import SheafCancel from './components/SheafCancel';
import Onboarding from './components/Onboarding';
import { PublicLandingPage } from './components/User/PublicLandingPage';
import CommunityForum from './components/Forum/CommunityForum';
import { logPageView } from './lib/analytics';
import { trackPageView as trackMetaPixelPageView, initMetaPixel } from './lib/metaPixel'; // ✅ add initMetaPixel import
import { FEATURE_FLAGS, isFeatureEnabled } from './config/featureFlags';
import { parseApiError } from './lib/apiErrors';
import { applyRouteSeoMetadata } from './lib/seoMetadata';

const authReturnToOptions = {
  returnTo: () => `${window.location.pathname}${window.location.search}`,
};

const ProtectedCommunityForum = withAuthenticationRequired(CommunityForum, authReturnToOptions);
const DEFAULT_PROFILE_READINESS_TIMEOUT_MS = 30000;

function getProfileReadinessTimeoutMs() {
  const configuredTimeout = Number(process.env.REACT_APP_PROFILE_READINESS_TIMEOUT_MS);
  return Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : DEFAULT_PROFILE_READINESS_TIMEOUT_MS;
}

function withTimeout(promise, timeoutMs, message, onTimeout) {
  let timeoutId;

  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      if (typeof onTimeout === 'function') {
        onTimeout();
      }
      reject(new Error(message));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    window.clearTimeout(timeoutId);
  });
}

function FeatureUnavailable({ featureName }) {
  return (
    <section
      role="alert"
      className="bg-olive bg-opacity-30 text-white min-h-screen p-6 rounded-xl shadow-inner"
      style={{ textAlign: 'center' }}
    >
      <h1 className="text-3xl font-bold text-yellow-400 mb-3">{featureName} is not enabled</h1>
      <p className="text-lg text-muted-foreground">
        This route is configured off for the current environment. Enable the matching
        REACT_APP feature flag to test it here.
      </p>
    </section>
  );
}

function NotFoundPage() {
  return (
    <section
      role="alert"
      className="bg-olive bg-opacity-30 text-white min-h-screen p-6 rounded-xl shadow-inner"
      style={{ textAlign: 'center' }}
    >
      <h1 className="text-3xl font-bold text-yellow-400 mb-3">Page not found</h1>
      <p className="text-lg text-muted-foreground">
        The page you requested does not exist or is not available in this environment.
      </p>
    </section>
  );
}

function StandaloneVoteUnavailable() {
  return (
    <section
      role="alert"
      className="bg-olive bg-opacity-30 text-white min-h-screen p-6 rounded-xl shadow-inner"
      style={{ textAlign: 'center' }}
    >
      <h1 className="text-3xl font-bold text-yellow-400 mb-3">Open a campaign to vote</h1>
      <p className="text-lg text-muted-foreground">
        Campaign voting requires a specific campaign. Open a group campaign from the Ideas tab and cast your vote there.
      </p>
    </section>
  );
}

function renderFeatureRoute(enabled, path, ComponentToRender, featureName, options) {
  const ProtectedComponent = withAuthenticationRequired(ComponentToRender, options);
  return (
    <Route
      path={path}
      render={(props) => (
        enabled
          ? <ProtectedComponent {...props} />
          : <FeatureUnavailable featureName={featureName} />
      )}
    />
  );
}

// ✅ functional wrapper to ensure initMetaPixel runs once on mount
function MetaPixelInitializer() {
  useEffect(() => {
    try {
      initMetaPixel();
    } catch (err) {
      console.warn("[MetaPixel] initialization failed", err);
    }
  }, []);
  return null;
}

function ButtonClickSound() {
  useEffect(() => {
    const handleClick = (event) => {
      if (!event.target) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const button = target.closest('button, [role="button"]');
      if (!button) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
        return;
      }
      const audio = new Audio('/button-click.mp3');
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return null;
}

export class App extends Component {
  static displayName = App.name;

  state = {
    authLoadingTimedOut: false,
    profileComplete: null,
    profileChecking: false,
    profileError: null,
  };

  componentDidMount() {
    window.addEventListener('profile-complete', this.handleProfileCompleted);
    if (this.props.location) {
      const path = this.getPath();
      applyRouteSeoMetadata(this.props.location.pathname);
      logPageView(path);
      trackMetaPixelPageView({ path });
    }

    this.checkProfileIfNeeded();
    this.scheduleAuthLoadingTimeout();
  }

  componentWillUnmount() {
    window.removeEventListener('profile-complete', this.handleProfileCompleted);
    this.clearAuthLoadingTimeout();
  }

  handleProfileCompleted = () => {
    this.setState({ profileComplete: true, profileChecking: false, profileError: null });
  };

  componentDidUpdate(prevProps) {
    if (!this.props.location) return;

    if (prevProps.auth0?.isLoading !== this.props.auth0?.isLoading) {
      if (this.props.auth0?.isLoading) {
        this.scheduleAuthLoadingTimeout();
      } else {
        this.clearAuthLoadingTimeout();
        if (this.state.authLoadingTimedOut) {
          this.setState({ authLoadingTimedOut: false });
        }
      }
    }

    const previousPath = prevProps.location
      ? `${prevProps.location.pathname}${prevProps.location.search || ''}`
      : null;
    const currentPath = this.getPath();

    if (previousPath !== currentPath) {
      applyRouteSeoMetadata(this.props.location.pathname);
      logPageView(currentPath);
      trackMetaPixelPageView({ path: currentPath });
    }

    const authChanged =
      prevProps.auth0?.isAuthenticated !== this.props.auth0?.isAuthenticated ||
      prevProps.auth0?.user?.sub !== this.props.auth0?.user?.sub;

    if (authChanged) {
      this.setState({ profileComplete: null, profileError: null }, this.checkProfileIfNeeded);
    } else {
      this.checkProfileIfNeeded();
    }
  }

  getPath() {
    const { pathname, search } = this.props.location;
    return `${pathname}${search || ''}`;
    }

  checkProfileIfNeeded = async () => {
    const { auth0 } = this.props;
    const { isAuthenticated, getAccessTokenSilently } = auth0 || {};
    if (!isAuthenticated) {
      if (this.state.profileChecking || this.state.profileComplete !== null || this.state.profileError) {
        this.setState({ profileChecking: false, profileComplete: null, profileError: null });
      }
      return;
    }

    if (this.state.profileChecking || this.state.profileComplete !== null || this.state.profileError) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      this.setState({
        profileError: 'The profile service is not configured.',
        profileChecking: false,
      });
      return;
    }

    this.setState({ profileChecking: true, profileError: null });

    const abortController = typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;

    try {
      const profileComplete = await withTimeout(
        (async () => {
          const token = await getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
          });

          const response = await fetch(`${apiUrl}/api/users/check-profile`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController?.signal,
          });

          if (!response.ok) {
            throw await parseApiError(response, `Profile check failed (${response.status})`);
          }
          const readiness = await response.json();
          if (typeof readiness !== 'boolean') {
            throw new Error('Profile check returned an invalid response.');
          }
          return readiness;
        })(),
        getProfileReadinessTimeoutMs(),
        'Profile check timed out.',
        () => abortController?.abort(),
      );

      this.setState({ profileComplete });
    } catch (error) {
      console.error('Error checking profile status:', error);
      this.setState({
        profileComplete: null,
        profileError: `${error.message || 'We could not check your profile.'} Please retry.`,
      });
    } finally {
      this.setState({ profileChecking: false });
    }
  };

  retryProfileCheck = () => {
    this.setState(
      { profileComplete: null, profileChecking: false, profileError: null },
      this.checkProfileIfNeeded,
    );
  };

  scheduleAuthLoadingTimeout = () => {
    if (!this.props.auth0?.isLoading || this.authLoadingTimeoutId) {
      return;
    }

    this.authLoadingTimeoutId = window.setTimeout(() => {
      this.authLoadingTimeoutId = null;
      if (this.props.auth0?.isLoading) {
        console.warn('Auth0 initialization exceeded the startup timeout; rendering public routes as signed out.');
        this.setState({ authLoadingTimedOut: true });
      }
    }, 10000);
  };

  clearAuthLoadingTimeout = () => {
    if (this.authLoadingTimeoutId) {
      window.clearTimeout(this.authLoadingTimeoutId);
      this.authLoadingTimeoutId = null;
    }
  };

  render() {
    const { isAuthenticated, isLoading } = this.props.auth0;
    const { authLoadingTimedOut, profileComplete } = this.state;
    const fullPlatformEnabled = isFeatureEnabled(FEATURE_FLAGS.fullPlatform);
    const campaignVotingEnabled = isFeatureEnabled(FEATURE_FLAGS.campaignVoting);
    const marketplaceListingsEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceListings);
    const jobsPayrollEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll);
    const sheafPurchaseEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase);
    const groupStripeFinanceEnabled = isFeatureEnabled(FEATURE_FLAGS.groupStripeFinance);
    const cashCheckStewardshipEnabled = isFeatureEnabled(FEATURE_FLAGS.cashCheckStewardship);
    const servicesEnabled = isFeatureEnabled(FEATURE_FLAGS.services);
    const hideChatTrigger = this.props.location?.pathname?.startsWith('/forum');

    if (isLoading && !authLoadingTimedOut) {
      return (
        <div style={{ textAlign: "center", paddingTop: "40px" }}>
          <h3>Loading...</h3>
        </div>
      );
    }

    const shouldCheckProfile = !authLoadingTimedOut && isAuthenticated;
    const profileNeedsSetup = shouldCheckProfile && profileComplete === false;
    const shouldRedirectToProfileSetup =
      profileNeedsSetup && this.props.location?.pathname !== '/profile-setup';

    if (shouldRedirectToProfileSetup) {
      return (
        <Layout>
          <Redirect to="/profile-setup" />
          <Chat showFloatingTrigger={!hideChatTrigger} />
        </Layout>
      );
    }

    return (
      <Layout>
        {/* Ensure Meta Pixel script runs */}
        <MetaPixelInitializer />
        <ButtonClickSound />

        {fullPlatformEnabled ? (
          <Switch>
            <Route exact path='/' component={Home} />
            <Route path='/learn' component={LearnPage} />
            <Route path='/media' component={MediaPage} />
            <Route path='/needs' component={withAuthenticationRequired(NeedsPage, authReturnToOptions)} />
            <Route path='/onboarding' component={Onboarding} />
            <Route path='/fetch-actions' component={withAuthenticationRequired(FetchActions)} />
            <Route path='/fetch-gigs' component={withAuthenticationRequired(FetchGigs)} />
            <Route path='/fetch-groups' component={FetchGroups} />
            <Route path='/fetch-invitations' component={withAuthenticationRequired(FetchInvitations)} />
            <Route path='/fetch-Policies' component={withAuthenticationRequired(FetchPolicies)} />
            <Route path='/fetch-positions' component={withAuthenticationRequired(FetchPositions)} />
            <Route path='/fetch-posts' component={withAuthenticationRequired(FetchPosts)} />
            <Route path='/fetch-subscriptions' component={withAuthenticationRequired(FetchSubscriptions)}/>
            <Route path='/fetch-topics' component={withAuthenticationRequired(FetchTopics)} />
            <Route path='/user-search' component={withAuthenticationRequired(UserSearch)}/>
            <Route path='/fetch-votes' component={withAuthenticationRequired(FetchVotes)} />
            <Route path="/join/church/:token" component={ChurchInviteJoinPage} />
            <Route path="/churches" component={FindChurchPage} />
            <Route path="/ministries/:groupId/support" component={MinistrySupportPage} />
            {renderFeatureRoute(groupStripeFinanceEnabled || cashCheckStewardshipEnabled, '/groups/:groupId/finance', GroupFinancePage, 'Group giving and receipts')}
            {renderFeatureRoute(groupStripeFinanceEnabled, '/stripe/connect-return', StripeConnectReturn, 'Group online giving')}
            {renderFeatureRoute(marketplaceListingsEnabled, '/marketplace/sellers/:sellerId', ForSalePage, 'Marketplace seller storefront')}
            {renderFeatureRoute(marketplaceListingsEnabled, ['/marketplace', '/for-sale'], ForSalePage, 'Marketplace listings')}
            {renderFeatureRoute(marketplaceListingsEnabled, '/group-assets/group/:groupId', GroupAssetPage, 'Group marketplace assets', authReturnToOptions)}
            {renderFeatureRoute(marketplaceListingsEnabled, '/user-inventory/user/:userId', withAuthenticationRequired(UserAssetPage), 'Marketplace inventory')}
            <Route path="/forum" component={ProtectedCommunityForum} />
            <Route path="/group-members/:groupId" component={withAuthenticationRequired(GroupMembersPage, authReturnToOptions)} />
            <Route path="/group/:groupId" component={withAuthenticationRequired(GroupPublicPage, authReturnToOptions)} />
            {renderFeatureRoute(jobsPayrollEnabled, '/jobs', JobsPage, 'Jobs and payroll')}
            {renderFeatureRoute(campaignVotingEnabled, '/Post-campaign', PostCampaign, 'Campaign creation')}
            {renderFeatureRoute(campaignVotingEnabled, '/Post-vote', StandaloneVoteUnavailable, 'Campaign voting')}
            <Route path='/Post-group' component={withAuthenticationRequired(PostGroup)}/>
            <Route path='/Post-subscription' component={withAuthenticationRequired(PostSubscription)} />
            {renderFeatureRoute(marketplaceListingsEnabled, '/real-estate', RealEstatePage, 'Real estate marketplace')}
            {renderFeatureRoute(servicesEnabled, '/services', withAuthenticationRequired(ServicesPage), 'Services')}
            <Route
              path='/profile-setup'
              component={ProfileSetupFormPage}
            />
            <Route path='/public' component={PublicLandingPage}/>
            <Route path='/user-profile' component={withAuthenticationRequired(UserProfile)}/>
            <Route path='/settings' component={withAuthenticationRequired(SettingsPage)}/>
            <Route path='/user-dashboard' component={withAuthenticationRequired(Dashboard)}/>
            {renderFeatureRoute(campaignVotingEnabled, '/dashboard/votes', VotingHistoryPage, 'Campaign voting', authReturnToOptions)}
            {renderFeatureRoute(marketplaceListingsEnabled, '/dashboard/trading-history', TradingHistoryPage, 'Marketplace history', authReturnToOptions)}
            <Route path='/dashboard' component={withAuthenticationRequired(Dashboard)}/>
            <Route path='/conflicts/:conflictId' component={withAuthenticationRequired(ConflictDetails)} />
            <Route path="/actions/:id/refinement" component={withAuthenticationRequired(ActionRefinementPage)} />
            <Route path="/policies/:policyId/refinement" component={withAuthenticationRequired(PolicyRefinementPage)} />
            <Route path="/positions/:positionId/refinement" component={withAuthenticationRequired(PositionRefinementPage)} />
            <Route path="/events/:eventId/refinement" component={withAuthenticationRequired(CalendarEventRefinementPage)} />
            <Route exact path="/events/:eventId" component={FellowshipEventPage} />
            {renderFeatureRoute(sheafPurchaseEnabled, '/sheaf/success', SheafSuccess, 'Sheaf purchase')}
            {renderFeatureRoute(sheafPurchaseEnabled, '/sheaf/cancel', SheafCancel, 'Sheaf purchase')}
            <Route path="*" component={NotFoundPage} />
          </Switch>
        ) : (
          <Switch>
            {/* Public landing page */}
            <Route
              exact
              path="/public"
              render={() => (isAuthenticated ? <Redirect to="/forum" /> : <PublicLandingPage />)}
            />

            <Route
              exact
              path="/forum"
              component={ProtectedCommunityForum}
            />

            <Route
              exact
              path="/learn"
              component={LearnPage}
            />

            <Route
              exact
              path="/media"
              component={MediaPage}
            />

            <Route
              exact
              path="/needs"
              component={withAuthenticationRequired(NeedsPage, authReturnToOptions)}
            />

            <Route
              path="/join/church/:token"
              component={ChurchInviteJoinPage}
            />

            <Route
              exact
              path="/churches"
              component={FindChurchPage}
            />

            <Route
              exact
              path="/ministries/:groupId/support"
              component={MinistrySupportPage}
            />

            <Route
              exact
              path="/user-search"
              component={withAuthenticationRequired(UserSearch)}
            />

            {renderFeatureRoute(marketplaceListingsEnabled, '/marketplace/sellers/:sellerId', ForSalePage, 'Marketplace seller storefront')}
            {renderFeatureRoute(marketplaceListingsEnabled, ['/marketplace', '/for-sale'], ForSalePage, 'Marketplace listings')}
            {renderFeatureRoute(marketplaceListingsEnabled, '/group-assets/group/:groupId', GroupAssetPage, 'Group marketplace assets', authReturnToOptions)}
            {renderFeatureRoute(marketplaceListingsEnabled, '/real-estate', RealEstatePage, 'Real estate marketplace')}
            {renderFeatureRoute(marketplaceListingsEnabled, '/user-inventory/user/:userId', withAuthenticationRequired(UserAssetPage), 'Marketplace inventory')}
            {renderFeatureRoute(jobsPayrollEnabled, '/jobs', JobsPage, 'Jobs and payroll')}
            {renderFeatureRoute(campaignVotingEnabled, '/Post-campaign', PostCampaign, 'Campaign creation')}
            {renderFeatureRoute(campaignVotingEnabled, '/Post-vote', StandaloneVoteUnavailable, 'Campaign voting')}
            {renderFeatureRoute(campaignVotingEnabled, '/dashboard/votes', VotingHistoryPage, 'Campaign voting', authReturnToOptions)}
            {renderFeatureRoute(marketplaceListingsEnabled, '/dashboard/trading-history', TradingHistoryPage, 'Marketplace history', authReturnToOptions)}
            {renderFeatureRoute(servicesEnabled, '/services', withAuthenticationRequired(ServicesPage), 'Services')}
            <Route path='/fetch-groups' component={FetchGroups} />
            <Route path='/Post-group' component={withAuthenticationRequired(PostGroup)} />
            {renderFeatureRoute(groupStripeFinanceEnabled || cashCheckStewardshipEnabled, '/groups/:groupId/finance', GroupFinancePage, 'Group giving and receipts')}
            {renderFeatureRoute(groupStripeFinanceEnabled, '/stripe/connect-return', StripeConnectReturn, 'Group online giving')}
            <Route path="/group-members/:groupId" component={withAuthenticationRequired(GroupMembersPage, authReturnToOptions)} />
            <Route path="/group/:groupId" component={withAuthenticationRequired(GroupPublicPage, authReturnToOptions)} />
            <Route exact path="/events/:eventId" component={FellowshipEventPage} />
            <Route path="/actions/:id/refinement" component={withAuthenticationRequired(ActionRefinementPage)} />
            <Route path="/policies/:policyId/refinement" component={withAuthenticationRequired(PolicyRefinementPage)} />
            <Route path="/events/:eventId/refinement" component={withAuthenticationRequired(CalendarEventRefinementPage)} />
            <Route path='/dashboard' component={withAuthenticationRequired(Dashboard)} />
            <Route path='/user-profile' component={withAuthenticationRequired(UserProfile)} />
            <Route path='/settings' component={withAuthenticationRequired(SettingsPage)} />
            {renderFeatureRoute(sheafPurchaseEnabled, '/sheaf/success', SheafSuccess, 'Sheaf purchase')}
            {renderFeatureRoute(sheafPurchaseEnabled, '/sheaf/cancel', SheafCancel, 'Sheaf purchase')}

            {/* Profile setup page */}
            <Route
              exact
              path="/profile-setup"
              render={() =>
                isAuthenticated ? <ProfileSetupFormPage /> : <Redirect to="/public" />
              }
            />

            <Route exact path="/">
              {profileNeedsSetup ? (
                <Redirect to="/profile-setup" />
              ) : isAuthenticated ? (
                <Home />
              ) : (
                <PublicLandingPage />
              )}
            </Route>

            <Route path="*" component={NotFoundPage} />
      </Switch>
        )}

        <Chat showFloatingTrigger={!hideChatTrigger} />
      </Layout>
    );
  }
}

export default withRouter(withAuth0(App));


  
